/**
 * Leitungen und Anschlüsse: Arten, Farben, Symbole und ihre 3D-Darstellung.
 * Leitungen werden in der App gezeichnet (nicht im Code) und gehören zum Haus-Zustand.
 */
import * as THREE from "three";
import type { Line, LineType, Point, PointType } from "@hausplaner/model";
import { floorBase } from "./constants";

export const LINE_INFO: Record<LineType, { name: string; color: string }> = {
  strom: { name: "Elektrik", color: "#e2a400" },
  wasser: { name: "Wasser kalt", color: "#2f7fd1" },
  warmwasser: { name: "Wasser warm", color: "#d64545" },
  abwasser: { name: "Abwasser", color: "#7d6650" },
  heizung: { name: "Heizung", color: "#e2701f" },
  netzwerk: { name: "Netzwerk", color: "#2f9e6b" },
};

/** Anschluss-Symbole; jedes gehört zu einer Leitungsart (Farbe, Ebene) */
export const POINT_INFO: Record<PointType, { name: string; layer: LineType; label: string; z: number }> = {
  steckdose: { name: "Steckdose", layer: "strom", label: "SD", z: 0.3 },
  schalter: { name: "Schalter", layer: "strom", label: "S", z: 1.05 },
  licht: { name: "Lampenauslass", layer: "strom", label: "L", z: 2.4 },
  verteiler: { name: "Verteiler / Dose", layer: "strom", label: "V", z: 1.5 },
  zapfstelle: { name: "Wasseranschluss", layer: "wasser", label: "W", z: 0.5 },
  abfluss: { name: "Abfluss", layer: "abwasser", label: "A", z: 0.4 },
  heizkoerper: { name: "Heizkörper-Anschluss", layer: "heizung", label: "H", z: 0.15 },
  netzwerkdose: { name: "Netzwerkdose", layer: "netzwerk", label: "N", z: 0.3 },
};

/** typische Höhen über Fußboden für neue Punkte */
export const HEIGHT_PRESETS: { name: string; z: number | "ceil" }[] = [
  { name: "Boden", z: 0.05 },
  { name: "Steckdose", z: 0.3 },
  { name: "Schalter", z: 1.05 },
  { name: "Arbeitsplatte", z: 1.15 },
  { name: "unter Decke", z: "ceil" },
];

const R = 0.035; // Leitungsradius (bewusst dicker als echt, damit man sie sieht)
const mats = new Map<string, THREE.MeshBasicMaterial>();
function lineMat(color: string, selected: boolean) {
  const k = color + (selected ? "!" : "");
  let m = mats.get(k);
  if (!m) {
    const c = new THREE.Color(color); if (selected) c.lerp(new THREE.Color("#ffffff"), 0.35);
    // depthTest aus: Leitungen sind "hervorgehoben" und auch durch Wände sichtbar
    m = new THREE.MeshBasicMaterial({ color: c, depthTest: false, transparent: true, opacity: 0.95 });
    mats.set(k, m);
  }
  return m;
}
const segGeo = new THREE.CylinderGeometry(1, 1, 1, 10, 1, true);
const jointGeo = new THREE.SphereGeometry(1, 12, 8);

/** Leitung als Rohr aus Zylindern + Kugeln an den Knicken */
export function buildLine(l: Pick<Line, "t" | "f" | "pts">, selected = false, dashed = false, risers = false): THREE.Group {
  const g = new THREE.Group();
  const m = lineMat(LINE_INFO[l.t].color, selected);
  const r = selected ? R * 1.6 : R;
  const v = l.pts.map(([x, y, z]) => new THREE.Vector3(x, floorBase(l.f, x, y) + z, -y));
  for (let i = 0; i < v.length; i++) {
    const s = new THREE.Mesh(jointGeo, m); s.position.copy(v[i]); s.scale.setScalar(r * 1.15); s.renderOrder = 20; g.add(s);
    if (i === 0) continue;
    const a = v[i - 1], b = v[i], len = a.distanceTo(b); if (len < 1e-4) continue;
    if (risers && l.pts[i][0] === l.pts[i - 1][0] && l.pts[i][1] === l.pts[i - 1][1]) { // senkrechtes Stück: im Grundriss sonst unsichtbar
      const sp = new THREE.Sprite(riserMat(l.t)); sp.position.copy(a.y > b.y ? a : b); sp.position.y += 0.02;
      sp.scale.setScalar(0.45); sp.renderOrder = 21; sp.raycast = () => undefined; g.add(sp);
    }
    const parts = dashed ? Math.max(1, Math.floor(len / 0.12)) : 1;
    for (let k = 0; k < parts; k++) {
      if (dashed && k % 2) continue;
      const t0 = k / parts, t1 = dashed ? (k + 1) / parts : 1;
      const p0 = a.clone().lerp(b, t0), p1 = a.clone().lerp(b, t1);
      const c = new THREE.Mesh(segGeo, m);
      c.position.copy(p0).lerp(p1, 0.5);
      c.scale.set(r, p0.distanceTo(p1), r);
      c.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), p1.clone().sub(p0).normalize());
      c.renderOrder = 20; g.add(c);
    }
  }
  return g;
}

const spriteCache = new Map<string, THREE.SpriteMaterial>();
/** Markierung „↕“ für senkrechte Leitungsstücke */
function riserMat(t: Line["t"]) {
  const k = "riser:" + t;
  let m = spriteCache.get(k);
  if (!m) {
    const cv = document.createElement("canvas"); cv.width = cv.height = 96; const g = cv.getContext("2d")!;
    g.beginPath(); g.arc(48, 48, 40, 0, Math.PI * 2); g.fillStyle = "#ffffff"; g.fill();
    g.lineWidth = 9; g.strokeStyle = LINE_INFO[t].color; g.stroke();
    g.fillStyle = LINE_INFO[t].color; g.font = '700 54px "IBM Plex Sans","Arial",sans-serif'; g.textAlign = "center"; g.textBaseline = "middle";
    g.fillText("↕", 48, 52);
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
    m = new THREE.SpriteMaterial({ map: tex, depthTest: false });
    spriteCache.set(k, m);
  }
  return m;
}
function symbolMat(t: PointType, selected: boolean) {
  const k = t + (selected ? "!" : "");
  let m = spriteCache.get(k);
  if (!m) {
    const info = POINT_INFO[t], color = LINE_INFO[info.layer].color;
    const cv = document.createElement("canvas"); cv.width = cv.height = 96; const g = cv.getContext("2d")!;
    g.beginPath(); g.arc(48, 48, 42, 0, Math.PI * 2); g.fillStyle = color; g.fill();
    g.lineWidth = selected ? 10 : 5; g.strokeStyle = selected ? "#1d2421" : "#ffffff"; g.stroke();
    g.fillStyle = "#ffffff"; g.font = '700 38px "IBM Plex Sans Condensed","Arial Narrow",sans-serif'; g.textAlign = "center"; g.textBaseline = "middle";
    g.fillText(info.label, 48, 51);
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
    m = new THREE.SpriteMaterial({ map: tex, depthTest: false });
    spriteCache.set(k, m);
  }
  return m;
}

/** Anschluss: Symbol (immer lesbar) + kleiner Stift zur Wand/zum Boden */
export function buildPoint(p: Pick<Point, "t" | "f" | "x" | "y" | "z">, selected = false): THREE.Group {
  const g = new THREE.Group();
  const y0 = floorBase(p.f, p.x, p.y);
  const sp = new THREE.Sprite(symbolMat(p.t, selected)); const s = selected ? 0.32 : 0.26;
  sp.scale.set(s, s, 1); sp.position.set(p.x, y0 + p.z, -p.y); sp.renderOrder = 22; g.add(sp);
  const dot = new THREE.Mesh(jointGeo, lineMat(LINE_INFO[POINT_INFO[p.t].layer].color, selected));
  dot.scale.setScalar(0.05); dot.position.set(p.x, y0 + p.z, -p.y); dot.renderOrder = 21; g.add(dot);
  return g;
}
